import BaseAccordion from 'components/Accordions/BaseAccordion';
import ConfirmationModal from 'components/Modals/ConfirmationModal';
import type { Alarm } from 'features/Alarms/types/Alarms.types';
import {
  Alert,
  AlertSeverityLabel,
  Custom_Alerts,
} from 'features/Weather/features/WeatherAlerts/types/alerts.types';
import { useGetAlarmsQuery } from 'services/Alarms/Alarms.api';
import { useGetAllActiveAlertsQuery } from 'services/alerts/alerts.api';
import { GlobalAction } from '../types/GlobalAction.type';

// Confirmation modal for warning about global action edit consequences.

type GlobalActionWarningMode = 'edit' | 'ipaws' | 'ipawsActual' | 'delete' | 'offline';

interface GlobalActionWarningModalProps {
  toWarn: boolean; // boolean state to trigger the modal
  globalAction: GlobalAction; // the global action being edited
  resolveWarning: (result: boolean) => void;
  mode: GlobalActionWarningMode;
  hasSentIpawsMessages?: boolean;
  /** The save sentence for an Actual IPAWS action. `null` when no action is Actual. */
  ipawsActualNotice?: string | null;
}

export default function GlobalActionWarningModal({
  toWarn,
  globalAction,
  resolveWarning,
  mode,
  hasSentIpawsMessages = false,
  ipawsActualNotice = null,
}: GlobalActionWarningModalProps) {
  const { data: alarmData } = useGetAlarmsQuery({ state: 1 });
  const { data: alertData } = useGetAllActiveAlertsQuery(undefined);

  //Count Related Alarms
  let relatedAlarms: Alarm[] = [];
  const allAlarms = alarmData?.data || [];
  allAlarms?.forEach((alarm) => {
    if (
      globalAction &&
      typeof globalAction.id !== 'undefined' &&
      Number(alarm.ga_identifier) === globalAction.id
    ) {
      relatedAlarms.push(alarm);
    }
  });

  //Count Related Alerts
  const allAlerts: Alert[] = alertData ?? [];
  // Type guard for Custom_Alerts while the shared Alert union includes a derived severityLabel.
  function isCustomAlert(
    alert: Alert,
  ): alert is Custom_Alerts & { severityLabel: AlertSeverityLabel } {
    return typeof (alert as Custom_Alerts).globalActionId !== 'undefined';
  }

  let relatedAlerts: Custom_Alerts[] = [];
  if (globalAction && typeof globalAction.id !== 'undefined') {
    allAlerts.forEach((alert) => {
      if (
        isCustomAlert(alert) &&
        alert.globalActionId != null &&
        Number(alert.globalActionId) === globalAction.id
      ) {
        relatedAlerts.push(alert);
      }
    });
  }

  const AlertsAccordion = (relatedAlerts: Custom_Alerts[]) => {
    return (
      <BaseAccordion
        header={`${relatedAlerts.length} Alerts Affected`}
        bodyBackground="var(--color-primary-shadow)"
      >
        <div className="customScrollbar-Y m-0 ps-3 overflow-y-auto" style={{ maxHeight: 200 }}>
          {relatedAlerts.map((alert) => (
            <p className="text-theme border-bottom border-1 w-100" key={alert.message_id}>
              {alert.smpp_message}
            </p>
          ))}
        </div>
      </BaseAccordion>
    );
  };

  const AlarmsAccordion = (relatedAlarms: Alarm[]) => {
    return (
      <BaseAccordion
        header={`${relatedAlarms.length} Alarms Affected`}
        bodyBackground="var(--color-primary-shadow)"
      >
        <div className="customScrollbar-Y m-0 ps-3 overflow-y-auto" style={{ maxHeight: 200 }}>
          {relatedAlarms.map((alarm) => (
            <p className="text-theme border-bottom border-1 w-100" key={alarm.id}>
              {alarm.headline}
            </p>
          ))}
        </div>
      </BaseAccordion>
    );
  };

  // Mode-specific content

  let title = '';
  let message = '';
  let confirmBtnTxt = 'Confirm';
  switch (mode) {
    case 'edit':
      title = `⚠️ Warning: Edits to ${globalAction?.title} will cause it to become inactive.`;
      message = `This change will immediately close all active alerts and pending alarms created by the ${globalAction?.title} Global Action.\n\nThis action is permanent. The following alerts and alarms will not be able to be restored after you confirm.`;
      confirmBtnTxt = 'Confirm';
      break;
    case 'ipaws':
      title = `⚠️ Warning: ${globalAction?.title} has sent IPAWS messages.`;
      message = 'Saving edits may cause the Global Action engine to replay its IPAWS action.';
      confirmBtnTxt = 'Confirm';
      break;
    case 'ipawsActual':
      title = 'Confirm the IPAWS send.';
      message = '';
      confirmBtnTxt = 'Save';
      break;
    case 'delete':
      title = `⚠️ Warning: Deleting ${globalAction?.title} is permanent!`;
      message = `This will immediately remove all active alerts and pending alarms created by the ${globalAction?.title} Global Action.\n\nThis action cannot be undone. The following alerts and alarms will be deleted and cannot be restored.`;
      confirmBtnTxt = 'Delete';
      break;
    case 'offline':
      title = `⚠️ Warning: Taking ${globalAction?.title} Offline.`;
      message = `This will deactivate the Global Action and close all active alerts and pending alarms it created.\n\nYou can reactivate it later, but the following alerts and alarms will be closed and cannot be restored.`;
      confirmBtnTxt = 'Confirm';
      break;
    default:
      title = `⚠️ Warning`;
      message = '';
      break;
  }

  if (mode === 'edit' && hasSentIpawsMessages) {
    message +=
      '\n\nThis Global Action also has sent IPAWS messages. Saving edits may replay its IPAWS action.';
  }

  if (ipawsActualNotice) {
    message = message ? `${message}\n\n${ipawsActualNotice}` : ipawsActualNotice;
  }

  return (
    <ConfirmationModal
      show={!!toWarn}
      handleShow={() => resolveWarning(false)}
      bodyTxt={
        <div className="text-center">
          <div>
            <span>{title}</span>
            <br />
            <br />
            <span>
              {message.split('\n').map((line, i) => (
                <span key={i}>
                  {line}
                  <br />
                </span>
              ))}
            </span>
          </div>
          <br />
          {relatedAlerts.length > 0 && AlertsAccordion(relatedAlerts)}
          {relatedAlarms.length > 0 && AlarmsAccordion(relatedAlarms)}
          {mode === 'ipawsActual' ? null : (
            <>
              <br />
              <span>
                The <strong>{globalAction?.title}</strong> cannot be restored.
              </span>
            </>
          )}
        </div>
      }
      customBodyTxtClassName="fs-5 mb-3"
      cancelBtnTxt={'Cancel'}
      confirmBtnTxt={confirmBtnTxt}
      confirmButtonVariant="danger"
      confirmHandler={() => resolveWarning(true)}
      cancelHandler={() => resolveWarning(false)}
    />
  );
}
